// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { Pressable, Modal as RNModal, StyleSheet, type ViewStyle } from 'react-native';
import { createTheme } from '../../design-system/theme';
import { useColorScheme } from '../../foundations/colorScheme';
import { callHistoryStrings } from '../strings';
import { CallHistoryDetailsPanel } from '../subscreens/CallHistoryDetailsPanel';
import type { CallHistoryActions, CallHistoryEntry } from '../types';

export interface CallDetailsModalProps {
  readonly visible: boolean;
  readonly entry: CallHistoryEntry | null;
  readonly actions?: CallHistoryActions;
  readonly onClose: () => void;
}

/**
 * Modal variant of the details panel for narrow viewports where the
 * right-rail layout is unavailable.
 */
export const CallDetailsModal: React.FC<CallDetailsModalProps> = ({
  visible,
  entry,
  actions,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);

  if (entry == null) {
    return null;
  }

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      accessibilityViewIsModal
      accessibilityLabel={callHistoryStrings.detailsTitle}
    >
      <Pressable
        style={[styles.overlay, { backgroundColor: theme.colors.overlay.scrim }]}
        onPress={onClose}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={[
            styles.shell,
            {
              backgroundColor: theme.colors.surface.primary,
              borderColor: theme.colors.border.secondary,
            },
          ]}
        >
          <CallHistoryDetailsPanel
            entry={entry}
            {...(actions !== undefined ? { actions } : {})}
            onClose={onClose}
          />
        </Pressable>
      </Pressable>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  } satisfies ViewStyle,
  shell: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '90%',
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
  } satisfies ViewStyle,
});
